import { default as React, useEffect, useState } from 'react';
import {
  DevHubAutocomplete,
  DevHubAutocompleteChangeReason,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import { gql, useLazyQuery } from '@apollo/client';

export interface ServiceBusiness {
  name: string;
  number: string;
  serviceStatus?: string | null;
  shortDescription?: string | null;
  state?: string | null;
}

const IopServiceBusinessQuery = gql`
  query GetIopServiceBusinessItems {
    resources {
      iopServiceBusinessItems {
        name
        number
        serviceStatus
        shortDescription
        state
      }
    }
  }
`;

interface Props {
  className?: string;
  helperText?: string;
  label?: string;
  multiple?: boolean;
  selectedServiceBusiness?: ServiceBusiness | null;
  setSelectedServiceBusiness?: React.Dispatch<
    React.SetStateAction<ServiceBusiness | null>
  >;
  selectedServiceBusinessItems?: ServiceBusiness[];
  setSelectedServiceBusinessItems?: React.Dispatch<
    React.SetStateAction<ServiceBusiness[]>
  >;
  required?: boolean;
}

/**
 * Dropdown selector for IOP Service Business items (cmdb_ci_service_business).
 */
export function ServiceBusinessSelect({
  className,
  helperText = 'Select a service business',
  label = 'Service Business',
  multiple = false,
  selectedServiceBusiness,
  setSelectedServiceBusiness,
  selectedServiceBusinessItems = [],
  setSelectedServiceBusinessItems,
  required = false,
}: Props) {
  const [serviceBusinessItems, setServiceBusinessItems] = useState<
    ServiceBusiness[]
  >([]);

  const [getServiceBusinessItems, { loading, error }] = useLazyQuery(
    IopServiceBusinessQuery,
    {
      errorPolicy: 'all',
    },
  );

  useEffect(() => {
    getServiceBusinessItems({})
      .then(result => {
        const data: ServiceBusiness[] | undefined =
          result?.data?.resources?.iopServiceBusinessItems;
        if (data) {
          setServiceBusinessItems(data);
        }
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Error fetching service business items', err);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (
    _event: Object,
    value: ServiceBusiness | ServiceBusiness[] | null,
    _reason: DevHubAutocompleteChangeReason,
  ) => {
    if (multiple) {
      if (setSelectedServiceBusinessItems) {
        setSelectedServiceBusinessItems(Array.isArray(value) ? value : []);
      }
      return;
    }

    if (setSelectedServiceBusiness) {
      setSelectedServiceBusiness(
        !Array.isArray(value) ? value : (value[0] ?? null),
      );
    }
  };

  if (error) {
    return (
      <DevHubTextField
        className={className}
        error
        label="Error"
        defaultValue="No Service Business Items Available"
        variant="outlined"
      />
    );
  }

  return (
    <DevHubAutocomplete
      multiple={multiple}
      className={className}
      loading={loading}
      loadingText="Loading.."
      options={serviceBusinessItems}
      value={multiple ? selectedServiceBusinessItems : selectedServiceBusiness}
      onChange={handleChange}
      getOptionLabel={(option: ServiceBusiness) =>
        option.number ? `${option.name} (${option.number})` : option.name
      }
      getOptionSelected={(option: ServiceBusiness, value: ServiceBusiness) =>
        option.number === value.number
      }
      renderInput={params => (
        <DevHubTextField
          {...params}
          label={label}
          helperText={helperText}
          margin="normal"
          variant="outlined"
          required={required}
          InputProps={params.InputProps}
        />
      )}
    />
  );
}
